import React, { useMemo } from 'react';
import { City, State } from 'country-state-city';
import { AutocompleteSelect, type AutocompleteOption } from './AutocompleteSelect';
import * as countriesLib from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

countriesLib.registerLocale(enLocale);

/**
 * Look up ISO 3166-1 alpha-2 country code from a full country name.
 */
function getCountryCode(name: string): string | null {
  const names = countriesLib.getNames('en');
  const entry = Object.entries(names).find(
    ([, n]) => n.toLowerCase() === name.toLowerCase()
  );
  return entry ? entry[0] : null;
}

interface CitySelectProps {
  value?: string;
  onChange: (value: string) => void;
  countryName?: string;
  stateName?: string;
  disabled?: boolean;
}

const CitySelect: React.FC<CitySelectProps> = ({
  value,
  onChange,
  countryName,
  stateName,
  disabled,
}) => {
  const cityOptions: AutocompleteOption[] = useMemo(() => {
    if (!countryName || !stateName) return [];

    const countryCode = getCountryCode(countryName);
    if (!countryCode) return [];

    const states = State.getStatesOfCountry(countryCode);
    const matchedState = states?.find(
      (s) => s.name.toLowerCase() === stateName.toLowerCase()
    );
    if (!matchedState) return [];

    const cities = City.getCitiesOfState(countryCode, matchedState.isoCode);
    if (!cities || cities.length === 0) return [];

    return cities
      .map((c) => ({ value: c.name, label: c.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [countryName, stateName]);

  return (
    <AutocompleteSelect
      value={value}
      onChange={onChange}
      options={cityOptions}
      placeholder={stateName ? 'Select city...' : 'Select state first...'}
      searchPlaceholder="Search cities..."
      emptyText="No city found."
      creatable
      disabled={disabled || !countryName || !stateName}
    />
  );
};

export { CitySelect };
